import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconWallet } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputField } from '@xver/components/Atoms';

import { useCreateChargeMutation } from '../hooks/useCreateChargeMutation';
import MethodPanel from './MethodPanel';
import TopupAmountForm from './TopupAmountForm';

interface TrueWalletMethodProps {
    defaultPhone?: string;
}

const PHONE_REGEX = /^0\d{8,9}$/;

export default function TrueWalletMethod({ defaultPhone }: TrueWalletMethodProps) {
    const { t } = useTranslation('topup');
    const mutation = useCreateChargeMutation();
    const [amount, setAmount] = useState<number>(50);
    const [agreed, setAgreed] = useState(false);
    const [phone, setPhone] = useState<string>(defaultPhone ?? '');
    const [phoneTouched, setPhoneTouched] = useState(false);

    const phoneError = useMemo(() => {
        if (!phone) return t('errors.phoneRequired');
        if (!PHONE_REGEX.test(phone)) return t('errors.phoneInvalid');
        return undefined;
    }, [phone, t]);

    const visiblePhoneError = phoneTouched ? phoneError : undefined;

    const handleSubmit = async () => {
        if (!phoneTouched) setPhoneTouched(true);
        if (phoneError) return;

        if (!window.Omise) {
            notifications.show({
                title: t('toast.errorTitle'),
                message: t('errors.omiseUnavailable'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
            return;
        }

        await new Promise<void>((resolve) => {
            window.Omise.createSource(
                'truemoney',
                {
                    amount: amount * 100,
                    currency: 'THB',
                    phone_number: phone,
                },
                async (statusCode, response) => {
                    if (statusCode !== 200) {
                        notifications.show({
                            title: t('toast.errorTitle'),
                            message: t('errors.sourceFailed'),
                            icon: <IconExclamationCircle size={18} color="#eb5757" />,
                        });
                        resolve();
                        return;
                    }
                    try {
                        const charge = await mutation.mutateAsync({
                            amount,
                            currency: 'THB',
                            redirectURL: window.location.origin,
                            sourceID: response.id,
                        });
                        window.location.href = charge.redirectURL;
                    } catch {
                        // Error already surfaced via mutation onError toast.
                    } finally {
                        resolve();
                    }
                }
            );
        });
    };

    return (
        <MethodPanel
            icon={IconWallet}
            title={t('methods.truewallet.title')}
            tagline={t('methods.truewallet.tagline')}
        >
            <TopupAmountForm
                amount={amount}
                onAmountChange={setAmount}
                agreed={agreed}
                onAgreedChange={setAgreed}
                submitting={mutation.isPending}
                onSubmit={handleSubmit}
                extraError={phoneError}
                extraHeader={
                    <InputField
                        size="md"
                        type="tel"
                        label={t('methods.truewallet.phone')}
                        placeholder={t('methods.truewallet.phonePlaceholder')}
                        value={phone}
                        onChange={(e) => {
                            setPhone(e.target.value.replace(/\D/g, ''));
                            if (!phoneTouched) setPhoneTouched(true);
                        }}
                        error={visiblePhoneError}
                    />
                }
            />
        </MethodPanel>
    );
}
